import { useRouter } from "next/router";

import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { AxiosError } from "axios";

import { yupResolver } from "@hookform/resolvers/yup";
import {
  LoginAppleIcon,
  LoginGoogleIcon,
  LoginKakaoIcon,
} from "@enkor/design/icons";
import useWebStorage, { STORAGE_KEY } from "@logics/hooks/useWebStorage";
import { NAVIGATION_ROUTE } from "@domains/common/constants/navigationRoute";
import { loginSchema } from "@domains/auth/constants/authFormSchema";
import { LoginSchemaType } from "@domains/auth/types/authTypes";
import { useCreateLoginMutation } from "@domains/auth/network/authMutations";
import { ToastError } from "@logics/utils/toast";
import { getColorButtonAnimation } from "@domains/common/utils/animationHelper";
import { ERROR_MESSAGE, ERROR_CODE } from "@domains/common/constants/errors";
import { useFetchUserProfileQuery } from "@domains/auth/network/authQueries";
import { ResponseUserTokenRefreshFip } from "@models/auth/data-contracts";
import { setAuthTokens } from "@domains/auth/utils/authTokenHandler";
import { ErrorResponseType } from "@models/client";
import { ErrorCodeType } from "@domains/common/types/common";

import FormInputForPassword from "../components/FormInputForPassword/FormInputForPassword";
import SubmitButton from "../components/SubmitButton/SubmitButton";
import FormInput from "../components/FormInput/FormInput";

import * as S from "./AdminComponent.style";

const AdminComponent = () => {
  const router = useRouter();

  const methods = useForm<LoginSchemaType>({
    resolver: yupResolver(loginSchema, { abortEarly: false }),
    criteriaMode: "all",
  });

  const [redirectAsPath, , removeRedirectAsPath] = useWebStorage(
    STORAGE_KEY.AUTH_REDIRECT,
    "",
    "localStorage",
  );

  const loginMutation = useCreateLoginMutation();
  const { refetch: fetchProfile } = useFetchUserProfileQuery({
    enabled: false,
  });

  const handleLoginSuccess = async ({
    accessToken,
    refreshToken,
  }: ResponseUserTokenRefreshFip) => {
    setAuthTokens({ accessToken, refreshToken });

    const { data, error } = await fetchProfile();

    if (error) {
      const { response } = error as AxiosError<ErrorResponseType>;

      if (response?.data?.errorCode) {
        handleLoginError(response?.data?.errorCode);
      }
    }
    // TODO nickname말고 다른 플래그 사용 필요
    if (data?.nickName) {
      const nextRoute = redirectAsPath
        ? `/${redirectAsPath}`
        : NAVIGATION_ROUTE.EVENT_LIST.HREF;

      router.push(nextRoute);
      removeRedirectAsPath();
    } else {
      router.push(NAVIGATION_ROUTE.EDIT_PROFILE.HREF);
    }
  };

  const handleLoginError = (errorCode: ErrorCodeType) => {
    switch (errorCode) {
      case ERROR_CODE.US003:
        methods.setError(
          "password",
          { type: "wrongPassword", message: ERROR_MESSAGE[ERROR_CODE.US003] },
          { shouldFocus: true },
        );
        break;
      case ERROR_CODE.US004:
        router.push(NAVIGATION_ROUTE.AUTH_COMPLETE.HREF);
        break;
      case ERROR_CODE.US005:
        router.push(NAVIGATION_ROUTE.EDIT_PROFILE.HREF);
        break;

      default:
        break;
    }
  };

  const handleSubmitLogin: SubmitHandler<LoginSchemaType> = ({
    email,
    password,
  }) => {
    loginMutation.mutate(
      { email, password },
      {
        onSuccess: ({ data }) => handleLoginSuccess(data),
        onError: ({ response }) => {
          const errorCode = response?.data?.errorCode;
          if (errorCode) handleLoginError(errorCode);
        },
      },
    );
  };

  const handleSocialLogin = (
    authProviderType: "google" | "kakao" | "apple",
  ) => {
    // NOTE: 필요할 떄 redirectUrl 넣어주기 (로컬에서는 일부러 http://localhost:3000으로 리다이렉트되도록 수정)
    const redirectUrl =
      process.env.NEXT_PUBLIC_NODE_ENV === "develop"
        ? `${process.env.NEXT_PUBLIC_SITE_URL}/${redirectAsPath || "eventList"}`
        : redirectAsPath || "eventList";
    removeRedirectAsPath();

    try {
      router.push(
        `${
          process.env.NEXT_PUBLIC_AUTH_API_BASE_URL
        }/v1/fip/oauth/${authProviderType}?authRedirect=${encodeURIComponent(
          `/${redirectUrl}`,
        )}`,
      );
    } catch (error) {
      const upperCaseType =
        authProviderType[0].toUpperCase() +
        authProviderType.slice(1).toLowerCase();
      ToastError(`${upperCaseType} login has failed, please try other email`);
    }
  };

  return (
    <S.Container>
      <S.Title name="h2">Enkoplay 어드민 로그인</S.Title>
      <S.Description name="m">어드민 로그인 페이지입니다.</S.Description>
      <FormProvider {...methods}>
        <S.AuthEntryForm onSubmit={methods.handleSubmit(handleSubmitLogin)}>
          <FormInput
            formId="email"
            type="email"
            label="Email"
            placeholder="your@email.com"
          />
          <FormInputForPassword
            hideIndicator
            formId="password"
            label="Password"
            placeholder="Put in your password"
          />
          <SubmitButton
            type="submit"
            isLoading={methods.formState.isSubmitting}
            isReadyToSubmit={methods.formState.isValid}
          />
          <S.OrSeparator>OR</S.OrSeparator>
          <S.GoogleButton
            type="button"
            onClick={() => {
              handleSocialLogin("google");
            }}
          >
            <LoginGoogleIcon strokeColor="transparent" />
            <S.ButtonText name="strongM">Continue with Google</S.ButtonText>
          </S.GoogleButton>
          <S.KakaoButton
            type="button"
            {...getColorButtonAnimation({
              hoverColor: "#F0D800",
              tapColor: "#DEC800",
            })}
            onClick={() => {
              handleSocialLogin("kakao");
            }}
          >
            <LoginKakaoIcon strokeColor="transparent" />
            <S.ButtonText name="strongM">Continue with Kakao</S.ButtonText>
          </S.KakaoButton>
          <S.AppleButton
            type="button"
            {...getColorButtonAnimation({
              hoverColor: "#4B4B4B",
              tapColor: "#5E5E5E",
            })}
            onClick={() => {
              handleSocialLogin("apple");
            }}
          >
            <LoginAppleIcon strokeColor="transparent" />
            <S.ButtonText name="strongM">Continue with Apple</S.ButtonText>
          </S.AppleButton>
        </S.AuthEntryForm>
      </FormProvider>
    </S.Container>
  );
};

export default AdminComponent;
